import { sceneCoordsToViewportCoords } from "@excalidraw/common";
import { useEffect, useRef, useState } from "react";

import {
  cascadeDeleteMindmapSubtree,
  collectDeletedMindmapElementIds,
} from "./drawolaMindmapDelete";
import {
  addChildMindmapNode,
  addSiblingMindmapNode,
} from "./drawolaMindmapElement";
import {
  getAllowedPlusDirections,
  isMindmapNode,
  type MindmapNodeElement,
} from "./drawolaMindmapModel";
import {
  findReparentTarget,
  reparentMindmapNode,
} from "./drawolaMindmapReparent";
import { MINDMAP_DATA_KEY } from "./types";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import type { PlusDirection } from "./types";

type PlusButton = {
  direction: PlusDirection;
  left: number;
  top: number;
};

type DropTargetRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type OverlayState = {
  node: MindmapNodeElement;
  buttons: PlusButton[];
  dropTarget: { node: MindmapNodeElement; rect: DropTargetRect } | null;
};

// Distance in screen pixels from the node edge to the center of the + button.
const PLUS_OFFSET = 20;

const computeButtons = (
  node: MindmapNodeElement,
  appState: AppState,
): PlusButton[] => {
  const allowed = getAllowedPlusDirections(node);
  return allowed.map((direction) => {
    let sceneX: number;
    let sceneY: number;
    switch (direction) {
      case "T":
        sceneX = node.x + node.width / 2;
        sceneY = node.y;
        break;
      case "B":
        sceneX = node.x + node.width / 2;
        sceneY = node.y + node.height;
        break;
      case "L":
        sceneX = node.x;
        sceneY = node.y + node.height / 2;
        break;
      case "R":
        sceneX = node.x + node.width;
        sceneY = node.y + node.height / 2;
        break;
    }
    const { x, y } = sceneCoordsToViewportCoords(
      { sceneX, sceneY },
      appState,
    );
    let left = x;
    let top = y;
    if (direction === "T") {
      top -= PLUS_OFFSET;
    } else if (direction === "B") {
      top += PLUS_OFFSET;
    } else if (direction === "L") {
      left -= PLUS_OFFSET;
    } else {
      left += PLUS_OFFSET;
    }
    return { direction, left, top };
  });
};

const computeDropTargetRect = (
  target: MindmapNodeElement,
  appState: AppState,
): DropTargetRect => {
  const tl = sceneCoordsToViewportCoords(
    { sceneX: target.x, sceneY: target.y },
    appState,
  );
  const br = sceneCoordsToViewportCoords(
    { sceneX: target.x + target.width, sceneY: target.y + target.height },
    appState,
  );
  return {
    left: tl.x,
    top: tl.y,
    width: br.x - tl.x,
    height: br.y - tl.y,
  };
};

const computeOverlay = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  isPointerDown: boolean,
): OverlayState | null => {
  if (appState.editingTextElement) {
    return null;
  }
  const selected = Object.entries(appState.selectedElementIds)
    .filter(([, on]) => on)
    .map(([id]) => id);
  if (selected.length !== 1) {
    return null;
  }
  const node = elements.find((el) => el.id === selected[0]);
  if (!node || !isMindmapNode(node)) {
    return null;
  }

  // While the pointer is held down on a non-root mindmap node, look for a
  // drop target whose bbox contains the dragged node's center. The target
  // is highlighted; + buttons are hidden so they don't get in the way.
  if (isPointerDown && node.customData) {
    const data = node.customData;
    if (data && (data as { drawolaMindmap?: { parentId: string | null } })) {
      const target = findReparentTarget(elements, node);
      if (target) {
        return {
          node,
          buttons: [],
          dropTarget: {
            node: target,
            rect: computeDropTargetRect(target, appState),
          },
        };
      }
    }
    // Pointer down but no drop target — still hide buttons during drag.
    return { node, buttons: [], dropTarget: null };
  }

  return {
    node,
    buttons: computeButtons(node, appState),
    dropTarget: null,
  };
};

export const DrawolaMindmapOverlay: React.FC<{
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}> = ({ excalidrawAPI }) => {
  const [state, setState] = useState<OverlayState | null>(null);
  // Refs so the onPointerUp callback can read fresh values without
  // re-subscribing on every change.
  const isPointerDownRef = useRef(false);
  const stateRef = useRef<OverlayState | null>(null);
  stateRef.current = state;
  // Snapshot of mindmap-element ids that are flagged isDeleted, used to
  // detect newly-deleted nodes between successive onChange callbacks.
  const prevDeletedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!excalidrawAPI) {
      return undefined;
    }
    const update = () => {
      const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
      const appState = excalidrawAPI.getAppState();

      // Cascade-delete any subtree whose root just got deleted by the user.
      const currentDeletedIds = collectDeletedMindmapElementIds(elements);
      const newlyDeleted: string[] = [];
      for (const id of currentDeletedIds) {
        if (!prevDeletedIdsRef.current.has(id)) {
          newlyDeleted.push(id);
        }
      }
      prevDeletedIdsRef.current = currentDeletedIds;
      if (newlyDeleted.length > 0) {
        cascadeDeleteMindmapSubtree(excalidrawAPI, newlyDeleted);
        // The cascade triggers another onChange that will refresh state;
        // skip the immediate setState to avoid rendering against the
        // not-yet-cascaded snapshot.
        return;
      }

      setState(computeOverlay(elements, appState, isPointerDownRef.current));
    };
    update();
    const unsubscribeChange = excalidrawAPI.onChange(update);
    const unsubscribeDown = excalidrawAPI.onPointerDown(() => {
      isPointerDownRef.current = true;
      update();
    });
    const unsubscribeUp = excalidrawAPI.onPointerUp(() => {
      isPointerDownRef.current = false;
      const current = stateRef.current;
      if (current?.dropTarget) {
        reparentMindmapNode(
          excalidrawAPI,
          current.node,
          current.dropTarget.node,
        );
      }
      update();
    });

    // Tab/Enter shortcuts on a selected mindmap node.
    // - Tab: add child on the outward side (root → R; non-root → self.side)
    // - Enter: add sibling below (no-op on root)
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLElement) {
        const tag = event.target.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          event.target.isContentEditable
        ) {
          return;
        }
      }
      const appState = excalidrawAPI.getAppState();
      if (appState.editingTextElement) {
        return;
      }
      const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
      const selected = Object.entries(appState.selectedElementIds)
        .filter(([, on]) => on)
        .map(([id]) => id);
      if (selected.length !== 1) {
        return;
      }
      const node = elements.find((el) => el.id === selected[0]);
      if (!node || !isMindmapNode(node)) {
        return;
      }
      const data = node.customData[MINDMAP_DATA_KEY];

      if (event.key === "Tab" && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
        // stopImmediatePropagation prevents Excalidraw's own keydown
        // listener from running its Tab behavior (open the
        // convertElementTypePopup that cycles rect/diamond/ellipse).
        event.preventDefault();
        event.stopImmediatePropagation();
        const side: "L" | "R" = data.side === "L" ? "L" : "R";
        addChildMindmapNode(excalidrawAPI, node, side);
        return;
      }
      if (event.key === "Enter" && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
        if (data.parentId === null) {
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        addSiblingMindmapNode(excalidrawAPI, node, "B");
      }
    };
    // Capture phase so we run BEFORE Excalidraw's own document/container
    // keydown listeners. Combined with stopImmediatePropagation above this
    // keeps Tab/Enter from triggering Excalidraw shortcuts when the user
    // is operating on a mindmap node.
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      unsubscribeChange();
      unsubscribeDown();
      unsubscribeUp();
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [excalidrawAPI]);

  if (!state || !excalidrawAPI) {
    return null;
  }

  return (
    <div className="drawola-mindmap-overlay" aria-hidden={false}>
      {state.dropTarget && (
        <div
          className="drawola-mindmap-drop-target"
          style={{
            left: `${state.dropTarget.rect.left}px`,
            top: `${state.dropTarget.rect.top}px`,
            width: `${state.dropTarget.rect.width}px`,
            height: `${state.dropTarget.rect.height}px`,
          }}
        />
      )}
      {state.buttons.map((btn) => (
        <button
          key={btn.direction}
          type="button"
          className={`drawola-mindmap-plus drawola-mindmap-plus-${btn.direction}`}
          aria-label={`Add ${btn.direction}`}
          title={
            btn.direction === "L" || btn.direction === "R"
              ? "Add child"
              : "Add sibling"
          }
          style={{ left: `${btn.left}px`, top: `${btn.top}px` }}
          onPointerDown={(event) => {
            // Prevent Excalidraw from handling the click and clearing the
            // selection before we get a chance to dispatch our action.
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
            const { node } = state;
            if (btn.direction === "L" || btn.direction === "R") {
              addChildMindmapNode(excalidrawAPI, node, btn.direction);
            } else {
              addSiblingMindmapNode(excalidrawAPI, node, btn.direction);
            }
          }}
        >
          +
        </button>
      ))}
    </div>
  );
};
